/**
 * PublishingCardCat — right-rail publishing summary for a Category.
 * Sibling of the session / quick-tap publishing cards, trimmed down
 * for what categories actually carry:
 *
 *   • Type   — "Quick Tap category" / "Standard category" pill
 *   • Slot   — "Appears in Select Interest" badge when on
 *   • Subitems — counts (subcategories, series, sessions/quick taps)
 *
 * Plus the canonical Activate / Deactivate toggle link at the foot.
 */
import React from 'react';
import RailCard, { RailCardHead } from '../../redesign/edit/RailCard';

export default function PublishingCardCat({
  state,
  counts = {},
  onToggleActive,
}) {
  const isActive = !!state.category_is_active;
  const isQuickTap = !!state.category_is_quick_tap;
  const isInSelectInterest = !!state.category_is_select_interest;

  return (
    <RailCard
      variant="publish"
      head={({ open, toggle }) => (
        <RailCardHead
          title="Publishing"
          variant="publish"
          open={open}
          onToggle={toggle}
          pill={
            <span
              className="font-condensed font-bold uppercase"
              style={{
                fontSize: 10,
                letterSpacing: '0.1em',
                padding: '3px 9px',
                borderRadius: 999,
                background: isActive ? '#75A29333' : '#9CA1A81F',
                color: isActive ? '#5C7E72' : '#9CA1A8',
                whiteSpace: 'nowrap',
              }}
            >
              {isActive ? 'Active' : 'Inactive'}
            </span>
          }
        />
      )}
    >
      <div style={{ padding: '14px 18px' }}>
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '10px 14px',
            margin: 0,
          }}
        >
          <div style={{ gridColumn: '1 / -1' }}>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Type
            </dt>
            <dd style={{ margin: '4px 0 0' }}>
              <span
                className="font-condensed font-bold uppercase"
                style={{
                  fontSize: 10,
                  letterSpacing: '0.1em',
                  padding: '3px 9px',
                  borderRadius: 999,
                  background: isQuickTap ? '#E58A6A33' : '#75A29333',
                  color: isQuickTap ? '#C4715B' : '#5C7E72',
                }}
              >
                {isQuickTap ? 'Quick Tap category' : 'Standard category'}
              </span>
              {isInSelectInterest && (
                <span
                  className="font-condensed font-bold uppercase"
                  style={{
                    marginLeft: 6,
                    fontSize: 10,
                    letterSpacing: '0.1em',
                    padding: '3px 9px',
                    borderRadius: 999,
                    background: '#1AA6B714',
                    color: '#1A8A98',
                  }}
                >
                  Select Interest
                </span>
              )}
            </dd>
          </div>

          {/* Per-bucket counts */}
          {[
            { label: 'Subcategories', value: counts.subcategories },
            { label: 'Series',        value: counts.series },
            { label: isQuickTap ? 'Quick taps' : 'Sessions', value: isQuickTap ? counts.quickTaps : counts.sessions },
          ].map((row) => (
            <div key={row.label}>
              <dt
                className="font-condensed font-bold uppercase text-text-muted"
                style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
              >
                {row.label}
              </dt>
              <dd
                className="text-navy"
                style={{ margin: '2px 0 0', fontSize: 12.5, fontWeight: 500 }}
              >
                {row.value ?? 0}
              </dd>
            </div>
          ))}
        </dl>

        <div
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTop: '1px solid #ECE3D2',
            textAlign: 'center',
          }}
        >
          <button
            type="button"
            onClick={() => onToggleActive?.(!isActive)}
            className="font-semibold cursor-pointer"
            style={{
              fontSize: 12,
              color: isActive ? '#C4715B' : '#5C7E72',
              background: 'transparent',
              border: 0,
              padding: 0,
            }}
          >
            {isActive ? 'Deactivate category' : 'Activate category'}
          </button>
        </div>
      </div>
    </RailCard>
  );
}
